/**
 * 納品明細 手入力画面 (v0.2.1)。
 *
 * 設計方針 (docs/design/v02-manual-entry-requirements.md §2-3「案A」準拠):
 *   - delivery_details も sales と同様、「店舗×月」全置換だと月内の他日付の納品まで
 *     巻き込んで削除してしまう。このため選択範囲を「店舗×日付」まで絞り込み、
 *     `POST /imports/data/delivery_details?mode=upsert&entry_scope=narrow` を使う。
 *   - 食材選択は GET /data/records?data_type=ingredient_master (店舗内の全履歴) を
 *     ingredient_code 単位で重複排除し、最新バージョンの名称で選択肢を構築する。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, AlertTriangle, Info, PackageCheck, Search } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { buildCsvText } from '@/lib/csv-export';
import { toast } from '@/stores/toast';
import { ConfirmDialog } from '@/components/Modal';
import { InputReadyButton } from '@/components/publish/InputReadyButton';

type Store = { store_id: string; store_code: string; store_name: string; status: 'active' | 'closed' };
type IngredientRecord = { ingredient_code: string; ingredient_name: string; unit_price: number | string | null; effective_from: string; effective_to: string | null };
type RecordsResponse = { count: number; rows: Array<Record<string, unknown>> };
type ImportError = { row: number; code?: string; column?: string; message: string };
type PreviewRow = { ingredient_code?: string; amount?: string; computed_amount?: number };
type PreviewResponse = { import_job_id: string; invalid_rows: number; errors: ImportError[]; preview: PreviewRow[] };
type CommitResponse = { inserted: number; error_count: number; errors: ImportError[]; status: string };

type DetailRow = {
  vendor_name: string;
  delivery_slip_no: string;
  ingredient_code: string;
  unit_price: string;
  quantity: string;
  amount: string;
  tax_rate: string;
  remarks: string;
};

const TAX_RATE_OPTIONS = [
  { value: '0.10', label: '10%（標準）' },
  { value: '0.08', label: '8%（軽減）' },
];

const today = (): string => new Date().toISOString().slice(0, 10);

/** GET /data/records は日付列を表示用に YYYY/MM/DD 形式で返す (data-browse.ts の toSlash)。
 *  date state (input type="date" 由来) は YYYY-MM-DD のため、比較・表示前にハイフン形式へ揃える */
const toIsoDate = (v: unknown): string => (v ? String(v).replace(/\//g, '-') : '');

const emptyRow = (): DetailRow => ({
  vendor_name: '', delivery_slip_no: '', ingredient_code: '', unit_price: '', quantity: '', amount: '', tax_rate: '0.08', remarks: '',
});

export function DeliveryDetailsEntryPage() {
  const qc = useQueryClient();

  const [storeId, setStoreId] = useState('');
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState<DetailRow[]>([]);
  const [errors, setErrors] = useState<ImportError[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResponse | null>(null);
  const [autoFilledIdx, setAutoFilledIdx] = useState<Set<number>>(new Set());
  const loadedKeyRef = useRef<string | null>(null);

  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });

  const { data: ingredientsRaw = [] } = useQuery<IngredientRecord[]>({
    queryKey: ['data-records', 'ingredient_master', storeId],
    queryFn: async () => {
      const params = new URLSearchParams({ data_type: 'ingredient_master', store_id: storeId });
      const res = await apiFetch<RecordsResponse>(`/data/records?${params}`);
      return res.rows as unknown as IngredientRecord[];
    },
    enabled: Boolean(storeId),
  });

  // ingredient_code 単位で重複排除し、最新 (effective_from が最大) のバージョンの名称を採用
  const ingredientOptions = useMemo(() => {
    const byCode = new Map<string, IngredientRecord>();
    for (const i of ingredientsRaw) {
      const existing = byCode.get(i.ingredient_code);
      if (!existing || toIsoDate(i.effective_from) > toIsoDate(existing.effective_from)) byCode.set(i.ingredient_code, i);
    }
    return [...byCode.values()].sort((a, b) => a.ingredient_code.localeCompare(b.ingredient_code));
  }, [ingredientsRaw]);

  const month = date ? date.slice(0, 7) : '';
  const existingQuery = useQuery<RecordsResponse>({
    queryKey: ['data-records', 'delivery_details', storeId, month],
    queryFn: () => {
      const params = new URLSearchParams({ data_type: 'delivery_details', store_id: storeId, month });
      return apiFetch<RecordsResponse>(`/data/records?${params}`);
    },
    enabled: Boolean(storeId && month),
  });

  // この日付に絞り込んだ既存行 (APIは月単位でしか取れないため、日付での絞込はクライアント側)
  const existingRowsForDate = useMemo(() => {
    if (!existingQuery.data || !date) return [];
    return existingQuery.data.rows.filter((r) => toIsoDate(r.delivery_date) === date);
  }, [existingQuery.data, date]);

  useEffect(() => {
    if (!storeId || !date || !existingQuery.data) return;
    const key = `${storeId}::${date}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;

    const loaded = existingRowsForDate.map((r) => ({
      vendor_name: r.vendor_name !== null && r.vendor_name !== undefined ? String(r.vendor_name) : '',
      delivery_slip_no: r.delivery_slip_no !== null && r.delivery_slip_no !== undefined ? String(r.delivery_slip_no) : '',
      ingredient_code: String(r.ingredient_code ?? ''),
      unit_price: r.unit_price !== null && r.unit_price !== undefined ? String(r.unit_price) : '',
      quantity: r.quantity !== null && r.quantity !== undefined ? String(r.quantity) : '',
      amount: r.amount !== null && r.amount !== undefined ? String(r.amount) : '',
      tax_rate: r.tax_rate !== null && r.tax_rate !== undefined ? String(r.tax_rate) : '0.08',
      remarks: r.remarks !== null && r.remarks !== undefined ? String(r.remarks) : '',
    }));
    setRows(loaded.length > 0 ? loaded : [emptyRow()]);
    setErrors(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, date, existingQuery.data]);

  useEffect(() => {
    if (!storeId || !date) {
      setRows([]);
      loadedKeyRef.current = null;
    }
  }, [storeId, date]);

  const selectedStore = stores.find((s) => s.store_id === storeId);

  const ingredientOptionsWithFallback = useMemo(() => {
    const opts = ingredientOptions.map((i) => ({ value: i.ingredient_code, label: `${i.ingredient_code} — ${i.ingredient_name}` }));
    const known = new Set(opts.map((o) => o.value));
    for (const r of rows) {
      if (r.ingredient_code && !known.has(r.ingredient_code)) {
        opts.push({ value: r.ingredient_code, label: `${r.ingredient_code}（無効/未登録）` });
        known.add(r.ingredient_code);
      }
    }
    return opts;
  }, [ingredientOptions, rows]);

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setAutoFilledIdx((prev) => {
      const next = new Set<number>();
      for (const i of prev) { if (i < idx) next.add(i); else if (i > idx) next.add(i - 1); }
      return next;
    });
  };
  const updateCell = (idx: number, field: keyof DetailRow, value: string) => {
    if (field === 'unit_price') {
      // ユーザーがマスタ補完値を上書きしたら、以後はハイライトしない (Design MEDIUM-3 反映)
      setAutoFilledIdx((prev) => { if (!prev.has(idx)) return prev; const next = new Set(prev); next.delete(idx); return next; });
    }
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx]!, [field]: value };
      return next;
    });
  };
  // 食材選択時、単価が未入力ならマスタ単価を初期値として補完する (あくまで下書き。保存前に編集・削除可、
  // 空欄のまま保存すれば backend が同じマスタ単価×数量で自動算出するため必須の操作ではない)。
  // Design レビュー MEDIUM-3 反映: 補完はサイレントに見えるため、直後の入力欄を一時的にハイライトする
  const updateIngredient = (idx: number, ingredientCode: string) => {
    setRows((prev) => {
      const next = [...prev];
      const current = next[idx]!;
      const master = ingredientOptions.find((i) => i.ingredient_code === ingredientCode);
      const masterUnitPrice = master?.unit_price;
      const filled = current.unit_price === '' && masterUnitPrice != null;
      next[idx] = {
        ...current,
        ingredient_code: ingredientCode,
        unit_price: filled ? String(masterUnitPrice) : current.unit_price,
      };
      if (filled) setAutoFilledIdx((p) => new Set(p).add(idx));
      return next;
    });
  };

  // v0.2.1 単価マスタ連携: 金額が空欄でも、食材マスタの単価×数量から自動算出されるため
  // (backendで算出不能な場合はcommit時にエラーとして返る)、食材が選択されていれば送信対象とする
  const isRowFilled = (row: DetailRow) => row.ingredient_code.trim() !== '';
  const filledRows = rows.filter(isRowFilled);
  const dataReady = Boolean(storeId && date) && loadedKeyRef.current === `${storeId}::${date}` && !existingQuery.isFetching;

  // Design レビュー HIGH-1/HIGH-2 反映 (2026-08-01): 従来は確認ダイアログを開いてから commit 直前に
  // 初めて preview API を叩いていたため、amount_not_determinable 等のエラーや自動算出金額を
  // 「保存しますか？」の確認前にユーザーへ提示できていなかった。preview を先に取得し、結果
  // (自動算出される金額を含む) を確認ダイアログに表示してから commit する流れに変更する。
  const openConfirm = async () => {
    if (!storeId || !selectedStore) { toast.error('店舗を選択してください'); return; }
    if (!date) { toast.error('納品日を選択してください'); return; }
    if (!dataReady) { toast.error('既存データの読込が完了していません。少し待ってから再度お試しください'); return; }
    if (filledRows.length === 0) { toast.error('入力内容がありません'); return; }

    const headers = ['store_code', 'delivery_date', 'vendor_name', 'delivery_slip_no', 'ingredient_code', 'unit_price', 'quantity', 'amount', 'tax_rate', 'remarks'];
    const csvRows = filledRows.map((row) => ({
      store_code: selectedStore.store_code,
      delivery_date: date,
      ...row,
    }));
    const csvText = buildCsvText(headers, csvRows, false);
    const file = new File([csvText], 'manual-delivery_details.csv', { type: 'text/csv' });

    setChecking(true);
    setErrors(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const token = localStorage.getItem('access_token');
      const previewRes = await fetch(`${API_BASE}/imports/data/delivery_details?mode=upsert&entry_scope=narrow`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      handleUnauthorized(previewRes.status);
      const previewBody = await previewRes.json();
      if (!previewRes.ok || !previewBody.success) {
        throw new ApiClientError(previewRes.status, previewBody.error?.code ?? 'UNKNOWN', previewBody.error?.message ?? '検証に失敗しました');
      }
      const preview = previewBody.data as PreviewResponse;
      if (preview.invalid_rows > 0) {
        setErrors(preview.errors);
        toast.error(`入力内容にエラーがあります (${preview.invalid_rows} 件)`);
        return;
      }
      setPreviewResult(preview);
      setConfirmOpen(true);
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '検証に失敗しました');
    } finally {
      setChecking(false);
    }
  };

  const doSave = async () => {
    if (!previewResult) return;
    setSaving(true);
    try {
      const commit = await apiFetch<CommitResponse>(`/imports/${previewResult.import_job_id}/commit`, { method: 'POST' });
      if (commit.error_count > 0) {
        setErrors(commit.errors);
        toast.error(`保存中にエラーが発生しました (${commit.error_count} 件)`);
        setConfirmOpen(false);
        return;
      }

      toast.success(`保存しました（${commit.inserted} 件）`);
      setConfirmOpen(false);
      setPreviewResult(null);
      loadedKeyRef.current = null;
      qc.invalidateQueries({ queryKey: ['data-records', 'delivery_details', storeId, month] });
      qc.invalidateQueries({ queryKey: ['data-summary'] });
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 確認ダイアログで自動算出される金額を可視化する (Design HIGH-2 反映)
  const autoCalculatedRows = (previewResult?.preview ?? []).filter((r) => r.computed_amount !== undefined);
  const autoCalculatedTotal = autoCalculatedRows.reduce((sum, r) => sum + (r.computed_amount ?? 0), 0);

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <PackageCheck size={26} className="text-brand-red" strokeWidth={1.75} />
          納品明細 手入力
        </h1>
        <p className="text-sm text-neutral-sub mt-1">
          店舗・納品日を選択し、業者・食材ごとの納品明細を入力します。CSV取込の補完用途を想定しており、選択した日付のデータのみを置き換えます（他日付のデータには影響しません）。
          金額を空欄にすると、<Link to="/masters/ingredients" className="text-brand-red hover:underline">食材マスタ</Link>に登録された単価×数量で自動算出されます（実際の納品書の単価・金額があればそちらを優先してください）
        </p>
        {/* ⚠️ この画面は「その日の納品をまとめて入力する」ための画面で、保存は選択日の全置換になる。
            そのため納品日を1日ずつ指定しないと中身が見えない。既に入っているデータを期間で見渡したり、
            業者名・食材で検索して1行だけ直したい場合は /data (取込済みデータ) が担当する
            (設計 §5-3 の相互導線。実運用で「日付を指定しないと何があるか分からない」との指摘を受けて追加)。 */}
        <p className="text-sm text-neutral-sub mt-2 flex items-center gap-1.5 flex-wrap">
          <Search size={14} aria-hidden className="shrink-0" />
          期間で見渡したい・業者名や食材で探して直したい場合は
          <Link to="/data?data_type=delivery_details" className="font-bold text-chrome-base underline hover:no-underline">
            取込済みデータ
          </Link>
          をお使いください（行単位での修正・削除ができます）
        </p>
      </div>

      <div className="border border-neutral-line rounded-lg bg-white p-4 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="dde-store" className="block text-xs text-neutral-sub mb-1">店舗 *</label>
          <select
            id="dde-store"
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            className="min-w-[180px] px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
          >
            <option value="">選択してください</option>
            {stores.map((s) => (
              <option key={s.store_id} value={s.store_id}>
                {s.store_name}{s.status === 'closed' ? '（停止中）' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="dde-date" className="block text-xs text-neutral-sub mb-1">納品日 *</label>
          <input
            id="dde-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
          />
        </div>
        {storeId && date && (
          <div className="text-xs text-neutral-sub flex items-center gap-1">
            <Info size={14} aria-hidden="true" />
            {existingQuery.isLoading || existingQuery.isFetching
              ? '既存データを確認中…'
              : `この日付には現在 ${existingRowsForDate.length} 件のデータがあります（他日付のデータは対象外）`}
          </div>
        )}
      </div>

      {storeId && date && (
        <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-panel text-neutral-sub">
                <tr>
                  <th className="w-8"></th>
                  <th className="text-left px-2 py-2 whitespace-nowrap">業者名</th>
                  <th className="text-left px-2 py-2 whitespace-nowrap">伝票番号</th>
                  <th className="text-left px-2 py-2 whitespace-nowrap">食材 <span className="text-warning-text">*</span></th>
                  <th className="text-left px-2 py-2 whitespace-nowrap">単価（食材選択時にマスタ値を補完）</th>
                  <th className="text-left px-2 py-2 whitespace-nowrap">数量</th>
                  <th className="text-left px-2 py-2 whitespace-nowrap">金額（税抜、円）</th>
                  <th className="text-left px-2 py-2 whitespace-nowrap">税率</th>
                  <th className="text-left px-2 py-2 whitespace-nowrap">備考</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-neutral-sub py-6">
                      「行を追加」ボタンで入力を開始してください
                    </td>
                  </tr>
                )}
                {rows.map((row, idx) => (
                  <tr key={idx} className="border-t border-neutral-line">
                    <td className="px-1">
                      <button type="button" onClick={() => removeRow(idx)} className="text-neutral-sub hover:text-danger-text p-1" aria-label="この行を削除">
                        <Trash2 size={14} />
                      </button>
                    </td>
                    <td className="px-1 py-1">
                      <input type="text" value={row.vendor_name} onChange={(e) => updateCell(idx, 'vendor_name', e.target.value)} placeholder="例: ○○食品"
                        className="w-full min-w-[110px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring" />
                    </td>
                    <td className="px-1 py-1">
                      <input type="text" value={row.delivery_slip_no} onChange={(e) => updateCell(idx, 'delivery_slip_no', e.target.value)}
                        className="w-full min-w-[100px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring" />
                    </td>
                    <td className="px-1 py-1">
                      <select value={row.ingredient_code} onChange={(e) => updateIngredient(idx, e.target.value)}
                        className="w-full min-w-[160px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring">
                        <option value="">選択してください</option>
                        {ingredientOptionsWithFallback.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <input type="number" min={0} value={row.unit_price} onChange={(e) => updateCell(idx, 'unit_price', e.target.value)} placeholder="220"
                        title={autoFilledIdx.has(idx) ? '食材マスタの単価を補完しました（編集可）' : undefined}
                        className={`w-full min-w-[80px] px-2 py-1.5 border rounded text-sm focus-ring ${autoFilledIdx.has(idx) ? 'bg-brand-gold/10 ring-1 ring-brand-gold/30 border-neutral-line' : 'border-neutral-line'}`} />
                    </td>
                    <td className="px-1 py-1">
                      <input type="number" min={0} value={row.quantity} onChange={(e) => updateCell(idx, 'quantity', e.target.value)} placeholder="30"
                        className="w-full min-w-[80px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring" />
                    </td>
                    <td className="px-1 py-1">
                      <input type="number" value={row.amount} onChange={(e) => updateCell(idx, 'amount', e.target.value)} placeholder="空欄=単価×数量で自動算出"
                        className="w-full min-w-[100px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring" />
                    </td>
                    <td className="px-1 py-1">
                      <select value={row.tax_rate} onChange={(e) => updateCell(idx, 'tax_rate', e.target.value)}
                        className="w-full min-w-[110px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring">
                        {TAX_RATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <input type="text" value={row.remarks} onChange={(e) => updateCell(idx, 'remarks', e.target.value)}
                        className="w-full min-w-[100px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" onClick={addRow} className="mt-3 flex items-center gap-1 text-sm text-brand-red hover:underline">
              <Plus size={14} /> 行を追加
            </button>
          </div>

          {errors && errors.length > 0 && (
            <div className="p-3 rounded bg-warning-bg border border-warning-border">
              <div className="flex items-start gap-2 text-sm text-warning-text font-semibold mb-1">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                入力内容にエラーがあります
              </div>
              <ul className="text-xs text-neutral-text space-y-0.5 pl-6 list-disc">
                {errors.slice(0, 20).map((e, i) => (
                  <li key={i}>{e.column ? `${e.column}: ` : ''}{e.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end items-center gap-2 pt-2 border-t border-neutral-line">
            {storeId && month && <InputReadyButton nodeId={storeId} targetMonth={month} dataType="delivery_details" />}
            <button
              type="button"
              onClick={openConfirm}
              disabled={saving || checking || !dataReady}
              title={!dataReady ? '既存データの読込中は保存できません' : undefined}
              className="px-5 py-2 rounded bg-brand-red text-white text-sm font-medium hover:bg-brand-red-hover disabled:opacity-50"
            >
              {checking ? '確認中…' : saving ? '保存中…' : '保存する'}
            </button>
          </div>
        </div>
      )}

      {(!storeId || !date) && (
        <div className="p-8 text-center text-neutral-sub border border-neutral-line rounded-lg bg-white">
          店舗と納品日を選択すると入力欄が表示されます
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="納品明細を保存しますか？"
        destructive={existingRowsForDate.length > 0 && filledRows.length < existingRowsForDate.length}
        confirmLabel="保存する"
        loading={saving}
        onClose={() => { if (!saving) { setConfirmOpen(false); setPreviewResult(null); } }}
        onConfirm={doSave}
        message={
          <div className="space-y-2">
            <p>
              {selectedStore?.store_name} の {date} 分として、<strong>{filledRows.length} 件</strong>を保存します。
            </p>
            <p className="text-xs text-neutral-sub">
              この日付の既存データのみを置き換えます（現在 {existingRowsForDate.length} 件 → 保存後 {filledRows.length} 件）。他日付のデータには影響しません。
            </p>
            {autoCalculatedRows.length > 0 && (
              <div className="p-2 rounded bg-brand-gold/10 border border-brand-gold/30 text-xs text-neutral-text">
                <p className="font-semibold mb-1">食材マスタの単価から金額を自動算出します（{autoCalculatedRows.length} 件、合計 ¥{autoCalculatedTotal.toLocaleString()}）</p>
                <ul className="space-y-0.5 pl-4 list-disc">
                  {autoCalculatedRows.slice(0, 5).map((r, i) => (
                    <li key={i}>{r.ingredient_code}: ¥{(r.computed_amount ?? 0).toLocaleString()}</li>
                  ))}
                  {autoCalculatedRows.length > 5 && <li>他 {autoCalculatedRows.length - 5} 件</li>}
                </ul>
              </div>
            )}
            {existingRowsForDate.length > 0 && filledRows.length < existingRowsForDate.length && (
              <div className="p-2 rounded bg-warning-bg border border-warning-border text-xs text-warning-text flex items-start gap-1.5">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                {existingRowsForDate.length - filledRows.length} 件が削除されます。よろしいですか？
              </div>
            )}
          </div>
        }
      />
    </div>
  );
}
